import React, { Fragment } from "react";
import { ActionType } from "../list/list/DataTable";
import { CashbackLogHistoryRequest, CashbackLogListResponse, CashbackLogRow, Source } from "../../http/protocol";
import { getContainer } from "../../ioc/IOCSetup";
import { EntityDataTable } from "../list/list/EntityDataTable";
import { PagerModel } from "../list/list/PagerModel";
import { ListUtils } from "../list/list/ListUtils";
import { withRouter } from "react-router";
import { ListView, ListViewProps, ListViewState } from "../ListView";
import { Form, FormListener } from "../form/Form";
import { FormGroup } from "../form/FormGroup";
import { FormInput } from "../form/FormInput";
import { Validators } from "../form/Validators";
import { Submit } from "../form/Submit";
import { SelectInput } from "../form/SelectInput";
import { CashbackLogService } from "../../services/CashbackLogService";
import { ColumnType } from "../list/list/ColumnType";
import { t } from "../../i18n";
import { SourceBadge } from "./SourceBadge";
import { FeatureFlags } from "../../feature-flags/FeatureFlagContext";
import { withFeatureFlag } from "../../feature-flags/withFeatureFlag";

interface State extends ListViewState<CashbackLogHistoryRequest> {
  form?: CashbackLogHistoryRequest;
  validationMap?: any;
  warningFlag?: boolean;
}

interface Props extends ListViewProps {
  cashbackV2Enabled: boolean;
}

function renderDisplayId(item: CashbackLogRow) {
  if (item.id === undefined || item.id === null) {
    return <span />;
  }
  return <span>{item.id}</span>;
}

export class CashbackLogListView extends ListView<State, CashbackLogHistoryRequest, Props> implements FormListener<CashbackLogHistoryRequest> {
  columns = [
    {
      property: "source",
      name: "Source",
      type: ColumnType.COMPONENT,
      component: (item: CashbackLogRow) => <SourceBadge source={item.source} />,
    },
    {
      property: "id",
      name: "Id",
      type: ColumnType.COMPONENT,
      component: (item: CashbackLogRow) => renderDisplayId(item),
    },
    { property: "status", name: "Status" },
    {
      property: "configId",
      name: "Config Id",
      type: ColumnType.LINK,
      getUrl: (item: CashbackLogRow) => (item.source === Source.V2 ? "/cashback/v2/config/" + item.configId : "/cashback/config/" + item.configId),
    },
    { property: "created", name: "Created", type: ColumnType.DATE, orderBy: "createdAt" },
    { property: "scheduled", name: "Scheduled", type: ColumnType.DATE },
    { property: "executionTimeSeconds", name: "Execution Time (Seconds)" },
    { property: "periodStart", name: "Period Start", type: ColumnType.DATE },
    { property: "periodEnd", name: "Period End", type: ColumnType.DATE },
  ];

  actions = [
    {
      id: "rerunPayouts",
      name: "Rerun Payouts",
      className: "btn btn-danger",
      iconClassName: "fa fa-user",
      type: ActionType.CONFIRMATION,
      show: (item: CashbackLogRow) => item.source === Source.V1,
    },
  ];

  service: CashbackLogService;

  constructor(props: Props) {
    super(props, "/cashback/log/list");
    this.state = { ...this.state, validationMap: {}, form: { ...this.state.listRequest } as any };
    this.service = getContainer().getCashbackLogService();
  }

  onSubmit(): void | Promise<any> {
    if (this.state.form) {
      const form = { ...this.state.form, offset: 0 };
      this.updateListState(form);
    }
  }

  onSubmitError(): void {}

  formDidUpdate(formModel: CashbackLogHistoryRequest, validationMap?: any): void {
    this.setState({ form: formModel, validationMap: validationMap });
  }

  onAction(item: CashbackLogRow, action: string): Promise<any> | undefined {
    if (action === "rerunPayouts" && item.source === Source.V1 && item.id !== undefined && item.id !== null) {
      return this.service.rerunPayouts(String(item.id)).then(() => this.refreshContent());
    }
  }

  pagerChanged(pager: PagerModel) {
    if (this.state.listRequest) {
      const listRequest = ListUtils.toListRequest<any>(this.state.listRequest, pager);
      this.updateListState(listRequest);
    }
  }

  getTitle() {
    return "List Cashback Logs";
  }

  renderContent() {
    if (!this.state.listRequest) {
      return <div />;
    }
    const columns = this.props.cashbackV2Enabled ? this.columns : this.columns.filter((column) => column.property !== "source");
    return (
      <Fragment>
        <div>
          <div className="wrapper wrapper-content">
            <div className={"card-box"}>
              <h4>{t("cashback.logs.filters", undefined, "Search Filters")}</h4>
              <Form
                formListener={this}
                modelObject={this.state.form}
                validationMap={this.state.validationMap}
              >
                <FormGroup className={"form-group"}>
                  <label>{t("cashback.logs.config-id", undefined, "Config Id")}</label>
                  <FormInput
                    model={"configId"}
                    className={"form-control"}
                    type={"integer"}
                    validators={[Validators.integer()]}
                  />
                </FormGroup>
                {this.props.cashbackV2Enabled && (
                  <FormGroup className={"form-group"}>
                    <label>{t("label.source", undefined, "Source")}</label>
                    <SelectInput
                      optional={true}
                      model={"source"}
                      className={"form-control"}
                      values={Object.keys(Source)}
                    />
                  </FormGroup>
                )}
                <Submit className={"btn btn-primary"}>{t("button.search", undefined, "Search")}</Submit>
              </Form>
            </div>
            {this.props.cashbackV2Enabled && this.state.warningFlag && (
              <div
                className="alert alert-warning mt-3"
                role="alert"
              >
                {t("cashback.logs.partial-warning", undefined, "Some cashback data could not be loaded. Results may be incomplete.")}
              </div>
            )}
            <EntityDataTable
              service={this.service}
              onPagerChange={this.pagerChanged.bind(this)}
              columns={columns}
              listRequest={this.state.listRequest}
              onResults={(response: CashbackLogListResponse) => this.setState({ warningFlag: response?.warningFlag === true })}
              onAction={this.onAction.bind(this)}
              actions={this.actions}
            />
          </div>
        </div>
      </Fragment>
    );
  }
}

const withCashbackV2Enabled = withFeatureFlag(FeatureFlags.CASHBACK_V2_ENABLED, "cashbackV2Enabled");

export default withRouter(withCashbackV2Enabled(CashbackLogListView));
